import Link from 'next/link';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PinterestIcon from '@mui/icons-material/Pinterest';
import XIcon from '@mui/icons-material/X';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { HOME_BRAND, HOME_FOOTER, HOME_FOOTER_SOCIAL_LINKS } from './home-landing-data';

const FOOTER_SOCIAL_BELOW_SPACING = 1.5;
const FOOTER_CTA_ABOVE_SOCIAL_SPACING = FOOTER_SOCIAL_BELOW_SPACING * 2;

const SOCIAL_ICONS = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  x: XIcon,
  pinterest: PinterestIcon,
};

const footerLinkSx = {
  color: 'rgba(255, 255, 255, 0.72)',
  textDecoration: 'none',
  letterSpacing: '0.12em',
  fontSize: 10,
  textTransform: 'uppercase',
  '&:hover': {
    color: 'common.white',
    textDecoration: 'underline',
  },
};

const footerTextSx = {
  color: 'rgba(255, 255, 255, 0.62)',
  letterSpacing: '0.1em',
  fontSize: 10,
  textTransform: 'uppercase',
  textShadow: '0 1px 6px rgba(0, 0, 0, 0.45)',
};

const socialIconButtonSx = {
  color: 'rgba(255, 255, 255, 0.62)',
  '&:hover': {
    color: 'common.white',
    bgcolor: 'transparent',
  },
};

export default function HomeLandingFooter() {
  const currentYear = new Date().getFullYear();
  const { establishedYear, companyName, webCredit } = HOME_FOOTER;

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        zIndex: 2,
        flexShrink: 0,
        py: { xs: 2, md: 2.5 },
        px: 2,
      }}
    >
      <Stack alignItems="center">
        <Stack
          direction="column"
          spacing={1}
          alignItems="center"
          sx={{ mb: FOOTER_CTA_ABOVE_SOCIAL_SPACING }}
        >
          <Button
            component={Link}
            href={HOME_BRAND.primaryCta.href}
            sx={{
              textTransform: 'uppercase',
              borderRadius: 0,
              letterSpacing: '0.2em',
              fontSize: { xs: 14, sm: 16 },
              minWidth: 220,
              py: 1.5,
              color: 'common.white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            {HOME_BRAND.primaryCta.label}
          </Button>
          <Button
            component={Link}
            href={HOME_BRAND.secondaryCta.href}
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
              fontSize: { xs: 14, sm: 16 },
              color: 'rgba(255,255,255,0.88)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.06)',
              },
            }}
          >
            {HOME_BRAND.secondaryCta.label}
          </Button>
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="center"
          sx={{ mb: FOOTER_SOCIAL_BELOW_SPACING }}
        >
          {HOME_FOOTER_SOCIAL_LINKS.map(({ label, href, icon }) => {
            const Icon = SOCIAL_ICONS[icon];
            return (
              <IconButton
                key={icon}
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                size="small"
                sx={socialIconButtonSx}
              >
                <Icon fontSize="small" />
              </IconButton>
            );
          })}
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.75, sm: 1.5 }}
          alignItems="center"
          justifyContent="center"
        >
          <Typography component="p" sx={footerTextSx}>
            © {establishedYear}–{currentYear} {companyName}
          </Typography>
          <Typography
            component="span"
            aria-hidden
            sx={{
              ...footerTextSx,
              display: { xs: 'none', sm: 'inline' },
              opacity: 0.5,
            }}
          >
            ·
          </Typography>
          <Box
            component={Link}
            href={webCredit.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={footerLinkSx}
          >
            {webCredit.label}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}
